/**
 * Lightweight S3 URL utilities
 * Replaces @mapbox/s3urls package with zero dependencies
 */

const S3_URL_PATTERNS = [
  // Path-style: https://s3.region.amazonaws.com/bucket/key
  /^https?:\/\/s3[.-]([a-z0-9-]+)?\.?amazonaws\.com\/([^/]+)\/(.+)$/,
  // Virtual-hosted-style: https://bucket.s3.region.amazonaws.com/key
  /^https?:\/\/([^.]+)\.s3[.-]([a-z0-9-]+)?\.?amazonaws\.com\/(.+)$/,
  // S3 protocol: s3://bucket/key
  /^s3:\/\/([^/]+)\/(.+)$/,
];

export interface S3UrlParts {
  Bucket: string;
  Key: string;
  Region?: string;
}

/**
 * Check if a URL is a valid S3 URL
 */
export function isValidS3Url(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  return S3_URL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Parse an S3 URL and extract bucket, key, and region
 */
export function parseS3Url(url: string): S3UrlParts | null {
  if (!url || typeof url !== 'string') return null;

  for (const pattern of S3_URL_PATTERNS) {
    const match = url.match(pattern);
    if (match) {
      // Different patterns have different capture groups
      if (pattern.source.includes('s3://')) {
        // s3://bucket/key
        return { Bucket: match[1], Key: match[2] };
      } else if (pattern.source.includes('\\/s3')) {
        // Path-style
        return {
          Region: match[1] || 'us-east-1',
          Bucket: match[2],
          Key: match[3],
        };
      } else {
        // Virtual-hosted-style
        return {
          Bucket: match[1],
          Region: match[2] || 'us-east-1',
          Key: match[3],
        };
      }
    }
  }

  return null;
}
